
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/base/Layout';
import { products, categories } from '../../mocks/products';

export default function ProductsPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);

  const filteredProducts = activeCategory === 'All'
    ? products
    : products.filter(p => p.category === activeCategory);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[40vh] md:h-[50vh] min-h-[300px] md:min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20lush%20green%20Egyptian%20agricultural%20fields%20with%20irrigation%20channels%20beautiful%20farmland%20landscape%20golden%20hour%20lighting%20professional%20drone%20photography%20vibrant%20colors&width=1920&height=800&seq=100&orientation=landscape"
            alt="Egyptian Agricultural Fields"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/70 via-emerald-900/50 to-emerald-900/80"></div>
        </div>
        <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
          <span className="inline-block px-4 py-1.5 bg-emerald-500/20 backdrop-blur-sm text-emerald-300 text-xs md:text-sm font-medium rounded-full mb-4 md:mb-6">
            Premium Export Quality
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            Our <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-[length:200%_auto] animate-gradient-text bg-clip-text text-transparent">Products</span>
          </h1>
          <p className="text-sm md:text-lg text-white/80 max-w-2xl mx-auto px-4">
            Discover our complete range of premium Egyptian agricultural products, carefully cultivated and prepared for global markets.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-4 md:py-8 bg-white/80 backdrop-blur-md sticky top-0 z-30 border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 md:px-6 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer relative overflow-hidden group ${
                  activeCategory === category
                    ? 'text-white shadow-lg shadow-emerald-500/30 border border-emerald-400/20'
                    : 'bg-white/80 backdrop-blur-md text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {activeCategory === category && (
                  <span className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 bg-[length:200%_100%] animate-gradient-move"></span>
                )}
                <span className="relative z-10">{category}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-10 md:py-16 bg-gradient-to-b from-white to-emerald-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6" data-product-shop>
            {filteredProducts.map((product) => (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                className="group relative bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
                onMouseEnter={() => setHoveredProduct(product.id)}
                onMouseLeave={() => setHoveredProduct(null)}
              >
                {/* Image Container */}
                <div className="relative h-40 md:h-64 w-full overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  {/* Category Badge */}
                  <span className="absolute top-2 md:top-4 left-2 md:left-4 px-2 md:px-3 py-0.5 md:py-1 bg-white/90 backdrop-blur-sm text-emerald-600 text-[10px] md:text-xs font-semibold rounded-full">
                    {product.category}
                  </span>

                  {/* Quick View Overlay */}
                  <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${hoveredProduct === product.id ? 'opacity-100' : 'opacity-0'}`}>
                    <span className="px-4 md:px-6 py-2 md:py-2.5 bg-white text-emerald-600 text-xs md:text-sm font-semibold rounded-full shadow-lg whitespace-nowrap">
                      View Details
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-3 md:p-5">
                  <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2 group-hover:text-emerald-600 transition-colors line-clamp-1">
                    {product.name}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500 line-clamp-2 mb-2 md:mb-4 hidden md:block">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] md:text-xs text-gray-400">
                      {product.specifications.season}
                    </span>
                    <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded-full relative overflow-hidden">
                      <span className="absolute inset-0 bg-emerald-100 group-hover:opacity-0 transition-opacity duration-300"></span>
                      <span className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 bg-[length:200%_100%] animate-gradient-move opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                      <i className="ri-arrow-right-line text-emerald-500 group-hover:text-white group-hover:translate-x-1 transition-all relative z-10 text-sm md:text-base"></i>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600 bg-[length:200%_100%] animate-gradient-move"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 md:w-96 h-64 md:h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-64 md:w-96 h-64 md:h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 md:mb-6">
            Ready to Import Premium Egyptian Produce?
          </h2>
          <p className="text-sm md:text-lg text-white/80 mb-6 md:mb-8">
            Contact our export team for pricing, availability, and shipping information.
          </p>
          <Link
            to="/contact"
            className="inline-block px-6 md:px-8 py-3 md:py-4 bg-white text-emerald-600 text-sm md:text-base font-semibold rounded-full hover:shadow-xl transition-all duration-300 whitespace-nowrap"
          >
            Request a Quote
          </Link>
        </div>
      </section>
    </Layout>
  );
}
